import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, LoadingState, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, SignOut, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fileDownloadUrl } from "@/lib/files";

export default function Visitors() {
    const { user } = useAuth();
    const [visitors, setVisitors] = useState([]);
    const [flats, setFlats] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", phone: "", purpose: "", flat_id: "", vehicle_no: "" });
    
    const isResident = user?.role === "resident";
    const pendingVisitors = visitors.filter(v => v.status === "REQUESTED");
    const activeVisitors = visitors.filter(v => v.status !== "REQUESTED" && v.status !== "REJECTED");

    async function load() {
        setLoading(true);
        try {
            const isResident = user?.role === "resident";
            
            // Fetch visitors
            const vResponse = await api.get("/visitors");
            setVisitors(vResponse.data);
            
            // Fetch flats - residents get only their flat
            if (isResident && user?.flat_id) {
                const fResponse = await api.get(`/flats/${user.flat_id}`);
                setFlats([fResponse.data]);
                // Auto-select resident's flat
                setForm(prev => ({ ...prev, flat_id: user.flat_id }));
            } else {
                const fResponse = await api.get("/flats");
                setFlats(fResponse.data);
            }
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [user]);

    async function submit(e) {
        e.preventDefault();
        try {
            const res = await api.post("/visitors", form);
            toast.success("Visitor logged");
            
            // Download QR code
            if (res.data.qr_code) {
                const link = document.createElement('a');
                link.href = res.data.qr_code;
                link.download = `visitor-qr-${res.data.id}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("QR code downloaded!");
            }
            
            setOpen(false);
            // Keep flat_id for residents when resetting form
            const keepFlatId = isResident && user?.flat_id ? user.flat_id : "";
            setForm({ name: "", phone: "", purpose: "", flat_id: keepFlatId, vehicle_no: "" });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function checkout(v) {
        try { await api.post(`/visitors/${v.id}/checkout`); toast.success("Checked out"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    async function approveVisitor(v) {
        try {
            await api.post(`/visitors/${v.id}/approve`);
            toast.success("Visitor approved and checked in");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function rejectVisitor(v) {
        if (!confirm(`Reject visitor ${v.name}?`)) return;
        try {
            await api.post(`/visitors/${v.id}/reject`);
            toast.success("Visitor entry rejected");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : "—";

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Gate"
                title="Visitors"
                description="Log visitors coming in and out. Committee sees all, residents see their own."
                actions={
                    <Button data-testid="add-visitor-btn" onClick={() => setOpen(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                        <Plus size={16} className="mr-1.5" /> Log visitor
                    </Button>
                }
            />

            {loading ? (
                <TableLoadingSkeleton rows={5} cols={7} />
            ) : (
                <>
                    {/* Pending Approvals - Only for residents */}
                    {isResident && pendingVisitors.length > 0 && (
                        <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-sm p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Clock size={20} className="text-amber-600" />
                                <h3 className="font-heading text-lg text-amber-900">Pending Approval ({pendingVisitors.length})</h3>
                            </div>
                            <div className="space-y-2">
                                {pendingVisitors.map(v => (
                                    <div key={v.id} className="bg-white rounded-sm p-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {v.photo_file_id && (
                                                <img src={fileDownloadUrl(v.photo_file_id)} alt="" data-testid={`visitor-photo-${v.id}`} className="w-12 h-12 rounded-sm object-cover border border-brand-line shrink-0" />
                                            )}
                                            <div>
                                                <p className="font-medium text-brand-ink">{v.name}</p>
                                                <p className="text-sm text-brand-inkSoft">Purpose: {v.purpose}</p>
                                                {v.vehicle_no && <p className="text-xs text-brand-inkSoft">Vehicle: {v.vehicle_no}</p>}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={() => approveVisitor(v)}
                                                data-testid={`approve-visitor-${v.id}`}
                                                size="sm"
                                                className="rounded-full bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                <CheckCircle size={14} className="mr-1" /> Approve
                                            </Button>
                                            <Button
                                                onClick={() => rejectVisitor(v)}
                                                data-testid={`reject-visitor-${v.id}`}
                                                size="sm"
                                                variant="outline"
                                                className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                                            >
                                                <XCircle size={14} className="mr-1" /> Reject
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Active Visitors Table */}
                    {activeVisitors.length === 0 && pendingVisitors.length === 0 ? (
                        <EmptyState title="No visitors logged" description="Log the first visitor at the gate to get started." />
                    ) : activeVisitors.length > 0 ? (
                        <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-brand-bg border-b border-brand-line text-left">
                                        {["Name", "Purpose", "Flat", "Vehicle", "Check-in", "Check-out", ""].map(h => (
                                            <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeVisitors.map((v) => (
                                        <tr key={v.id} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                            <td className="py-3 px-4 font-medium text-brand-ink">
                                                {v.name}
                                                {v.phone && <div className="text-xs text-brand-inkSoft mt-0.5">{v.phone}</div>}
                                            </td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{v.purpose}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{v.flat ? `${v.flat.block}-${v.flat.number}` : "—"}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{v.vehicle_no || "—"}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{fmt(v.check_in)}</td>
                                            <td className="py-3 px-4">
                                                {v.check_out ? <span className="text-brand-inkSoft">{fmt(v.check_out)}</span> : <Chip variant="warn">On premises</Chip>}
                                            </td>
                                            <td className="py-3 px-4 text-right">
                                                {!v.check_out && (
                                                    <button data-testid={`checkout-visitor-${v.id}`} onClick={() => checkout(v)} className="text-brand-action hover:underline text-xs font-medium inline-flex items-center gap-1">
                                                        <SignOut size={14} /> Check-out
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Log visitor</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Name</Label>
                                <Input data-testid="visitor-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2"><Label>Phone</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="space-y-2"><Label>Purpose</Label>
                            <Input data-testid="visitor-purpose-input" required value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Delivery, guest, service..." className="rounded-sm border-brand-line" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Visiting flat</Label>
                                {isResident ? (
                                    <Input 
                                        value={flats[0] ? `${flats[0].block}-${flats[0].number}` : ""} 
                                        disabled 
                                        className="rounded-sm border-brand-line bg-gray-50" 
                                    />
                                ) : (
                                    <Select value={form.flat_id} onValueChange={(v) => setForm({ ...form, flat_id: v })}>
                                        <SelectTrigger data-testid="visitor-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                                        <SelectContent>{flats.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}</SelectContent>
                                    </Select>
                                )}
                            </div>
                            <div className="space-y-2"><Label>Vehicle no. (optional)</Label>
                                <Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="visitor-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Log entry</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
